import type { CargoInvoiceRow } from '../api/types';

export function invoiceStatusBadge(status: string): string {
  switch (status) {
    case 'paid':
      return 'badge badge-success';
    case 'pending':
      return 'badge badge-pending';
    case 'expired':
      return 'badge badge-blocked';
    case 'aml_failed':
      return 'badge badge-blocked';
    default:
      return 'badge';
  }
}

export function invoiceStatusLabel(status: string): string {
  switch (status) {
    case 'paid':
      return 'Paid';
    case 'pending':
      return 'Pending';
    case 'expired':
      return 'Expired';
    case 'aml_failed':
      return 'AML failed';
    default:
      return status;
  }
}

export function companyStatusBadge(status: string): string {
  if (status === 'active') return 'badge badge-success';
  if (status === 'pending') return 'badge badge-pending';
  return 'badge badge-blocked';
}

export function companyStatusLabel(status: string): string {
  if (status === 'active') return 'Active';
  if (status === 'pending') return 'Pending approval';
  if (status === 'suspended') return 'Suspended';
  return status;
}

export function networkBadgeClass(network: string): string {
  return network === 'solana' ? 'cargo-network cargo-network-sol' : 'cargo-network cargo-network-tron';
}

export function formatUsdt(amount: string): string {
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  const frac = Math.abs(n - Math.trunc(n));
  if (frac < 1e-9) {
    return Math.trunc(n).toLocaleString('en-US', { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function invoicePaymentUrl(hashId: string, payBase: string): string {
  const base = payBase.replace(/\/$/, '');
  return `${base}/invoice/${hashId}`;
}

export function filterCompanies<T extends { name: string; company_code: string }>(
  rows: T[],
  query: string,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter(
    (r) => r.name.toLowerCase().includes(q) || r.company_code.toLowerCase().includes(q),
  );
}

export function splitStatusBadge(split: string | null | undefined): string {
  if (!split) return 'badge';
  if (split === 'completed') return 'badge badge-success';
  if (split === 'failed') return 'badge badge-blocked';
  return 'badge badge-pending';
}

export function splitStatusLabel(split: string | null | undefined): string {
  if (!split) return '—';
  if (split === 'completed') return 'Split done';
  if (split === 'failed') return 'Split failed';
  if (split === 'pending') return 'Split pending';
  if (split === 'processing') return 'Splitting…';
  return split;
}

export function canRetryCargoInvoiceSplit(invoice: {
  status: string;
  network: string;
  split_status?: string | null;
}): boolean {
  if (invoice.status !== 'paid' || invoice.network !== 'tron') return false;
  return invoice.split_status !== 'completed';
}

export function tronscanTxUrl(tx: string | null | undefined): string | null {
  if (!tx) return null;
  return `https://tronscan.org/#/transaction/${encodeURIComponent(tx)}`;
}

export function formatUsdtVolume(val: string | null | undefined): string {
  const n = Number.parseFloat(val ?? '');
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

export function formatCountryLabel(code: string | null | undefined): string {
  if (!code) return '—';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

export function filterInvoices(rows: CargoInvoiceRow[], query: string): CargoInvoiceRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter(
    (r) =>
      r.hash_id.toLowerCase().includes(q) ||
      (r.company_name?.toLowerCase().includes(q) ?? false) ||
      (r.cargo_id?.toLowerCase().includes(q) ?? false) ||
      r.status.toLowerCase().includes(q),
  );
}
